import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { LandingLogo } from '@app/landing/LandingLogo';
import { PublicFooter } from '@app/landing/PublicFooter';
import { initAuthBgFX } from './authEffects';
import { applyFxLiteClass, isFxLite, removeFxLiteClass } from '@utils/fxLite';
import { LangSwitcher } from '@components/LangSwitcher';
import './auth.css';

type AuthPageShellProps = {
  title: string;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
};

export const AuthPageShell = observer(function AuthPageShell({ title, headerExtra, children }: AuthPageShellProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lite = isFxLite();

  useEffect(() => {
    document.documentElement.classList.add('auth-active');
    applyFxLiteClass();
    return () => {
      document.documentElement.classList.remove('auth-active');
      removeFxLiteClass();
    };
  }, []);

  useEffect(() => {
    if (lite) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    return initAuthBgFX(canvas);
  }, [lite]);

  return (
    <div className="auth-page font-sans antialiased min-h-screen flex flex-col overflow-x-hidden relative">
      {lite ? (
        // Phones: one gradient layer, no blur, no canvas (iOS Safari freezes on the full stack).
        <div className="auth-bg-lite pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden="true" />
      ) : (
        <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
          <canvas ref={canvasRef} id="bgFX" className="absolute inset-0 w-full h-full" />
          <div className="bg-motion" />
          <div
            className="blob -top-40 -left-32 w-[42rem] h-[42rem] bg-brand/25 blur-[140px]"
            style={{ animation: 'authDrift1 26s ease-in-out infinite' }}
          />
          <div
            className="blob top-1/4 -right-44 w-[40rem] h-[40rem] bg-indigo-600/20 blur-[150px]"
            style={{ animation: 'authDrift2 32s ease-in-out infinite' }}
          />
          <div
            className="blob bottom-0 right-1/4 w-[32rem] h-[32rem] bg-blue-600/16 blur-[150px]"
            style={{ animation: 'authDrift1 36s ease-in-out infinite reverse' }}
          />
        </div>
      )}

      <div className="absolute top-4 right-5 z-20">
        <LangSwitcher />
      </div>

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center gap-5 mb-8">
            <Link
              to="/"
              className="auth-reveal flex items-center justify-center gap-3"
              style={{ animationDelay: '0.02s' }}
            >
              <LandingLogo size="lg" frame />
            </Link>
            <h1 className="auth-reveal text-2xl sm:text-3xl font-extrabold" style={{ animationDelay: '0.08s' }}>
              {title}
            </h1>
            {headerExtra}
          </div>

          <div
            className="auth-reveal glass rounded-3xl border border-white/10 shadow-2xl shadow-black/50 p-6 sm:p-8"
            style={{ animationDelay: '0.14s' }}
          >
            {children}
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
});
